<script setup lang="ts">
import { computed } from 'vue'
import { 
  House, 
  User, 
  Settings, 
  Search, 
  Plus, 
  Minus, 
  SquarePen, 
  Trash2, 
  Save, 
  Download,
  Upload,
  Eye,
  EyeOff,
  Heart,
  Star,
  Check,
  X,
  CircleAlert,
  Info,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Menu,
  Ellipsis,
  EllipsisVertical,
  Palette,
  // WMS 핵심 아이콘들
  Package,
  PackageCheck,
  PackageX,
  Truck,
  Warehouse,
  Boxes,
  Archive,
  ClipboardList,
  ClipboardCheck,
  FileText,
  ChartBar,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  MapPin,
  Table,
  List,
  ListFilter,
  ArrowUpWideNarrow,
  ArrowDownWideNarrow,
  Scale,
  // 추가 아이콘들
  Server,
  Database,
  Link,
  ArrowLeftRight,
  CircleQuestionMark,
  Monitor,
  Users,
  Code,
  FileKey,
  Cpu,
  Rocket,
  FileSpreadsheet,
  Activity,
  MessageCircle,
  Book,
  LogIn,
  LogOut,
  // 편집 관련
  Copy,
  Clipboard,
  Undo2,
  Redo2,
  Pencil,
  // 파일/폴더 관련
  Folder,
  FolderPlus,
  FolderOpen,
  FilePlus,
  FileSearch,
  FilePen,
  FileMinus,
  // 통신/알림 관련
  Mail,
  Send,
  Phone,
  Bell,
  BellOff,
  MessageSquare,
  // 화살표/방향 관련
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  RotateCw,
  RotateCcw,
  RefreshCw,
  Move,
  // 상태/피드백 관련
  CircleCheckBig,
  CircleX,
  Circle,
  // 보안 관련
  Lock,
  LockOpen,
  Shield,
  ShieldCheck,
  ShieldAlert,
  // 사용자 관리 관련
  UserPlus,
  UserMinus,
  UserCheck,
  UserX,
  CircleUser,
  // 액션 관련
  Power,
  Zap,
  Target,
  Flag,
  Funnel,
  // 조직/팀 관련
  Building,
  Briefcase,
  Globe,
  // 기타 유용한 아이콘
  Printer,
  Share2,
  ExternalLink,
  Maximize2,
  Minimize2,
  MoveVertical,
  MoveHorizontal,
  Sun,
  Moon,
  type LucideIcon
} from 'lucide-vue-next'

// 확장 가능한 아이콘 타입
type BaseIconName = 
  | 'house' | 'user' | 'settings' | 'search' | 'plus' | 'minus'
  | 'squarePen' | 'trash2' | 'save' | 'download' | 'upload'
  | 'eye' | 'eyeOff' | 'heart' | 'star' | 'check' | 'x'
  | 'circleAlert' | 'info' | 'chevronDown' | 'chevronUp'
  | 'chevronLeft' | 'chevronRight' | 'menu' | 'ellipsis' | 'ellipsisVertical'
  | 'palette'
  // WMS 핵심 아이콘들
  | 'package' | 'packageCheck' | 'packageX'
  | 'truck' | 'warehouse' | 'boxes'
  | 'archive' | 'clipboardList' | 'clipboardCheck'
  | 'fileText' | 'chartBar'
  | 'trendingUp' | 'trendingDown' | 'calendar' | 'clock'
  | 'mapPin' | 'table' | 'list' | 'listFilter'
  | 'arrowUpWideNarrow' | 'arrowDownWideNarrow' | 'scale'
  // 추가 아이콘들
  | 'server' | 'database' | 'link' | 'arrowLeftRight' | 'circleQuestionMark'
  | 'monitor' | 'users' | 'code' | 'fileKey' | 'cpu'
  | 'rocket' | 'fileSpreadsheet' | 'activity' | 'messageCircle' | 'book'
  // 인증 관련 아이콘들
  | 'logIn' | 'logOut'
  // 편집 관련 아이콘들
  | 'copy' | 'clipboard' | 'undo2' | 'redo2' | 'pencil'
  // 파일/폴더 관련 아이콘들
  | 'folder' | 'folderPlus' | 'folderOpen' | 'filePlus' | 'fileSearch' | 'filePen' | 'fileMinus'
  // 통신/알림 관련 아이콘들
  | 'mail' | 'send' | 'phone' | 'bell' | 'bellOff' | 'messageSquare'
  // 화살표/방향 관련 아이콘들
  | 'arrowUp' | 'arrowDown' | 'arrowLeft' | 'arrowRight' | 'rotateCw' | 'rotateCcw' | 'refreshCw' | 'move'
  // 상태/피드백 관련 아이콘들
  | 'circleCheckBig' | 'circleX' | 'circle'
  // 보안 관련 아이콘들
  | 'lock' | 'lockOpen' | 'shield' | 'shieldCheck' | 'shieldAlert'
  // 사용자 관리 관련 아이콘들
  | 'userPlus' | 'userMinus' | 'userCheck' | 'userX' | 'circleUser'
  // 액션 관련 아이콘들
  | 'power' | 'zap' | 'target' | 'flag' | 'funnel'
  // 조직/팀 관련 아이콘들
  | 'building' | 'briefcase' | 'globe'
  // 기타 유용한 아이콘들
  | 'printer' | 'share2' | 'externalLink' | 'maximize2' | 'minimize2' | 'moveVertical' | 'moveHorizontal'
  | 'sun' | 'moon'

// 커스텀 아이콘 확장을 위한 타입
type CustomIconName = string // 나중에 확장 가능

type IconName = BaseIconName | CustomIconName

type StyleType =
  | 'default'   // 기본 스타일
  | 'primary'   // 강조 스타일 (파랑)
  | 'success'   // 성공 스타일 (초록)
  | 'warning'   // 경고 스타일 (주황)
  | 'danger'    // 위험 스타일 (빨강)

type SizeType = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const props = withDefaults(
  defineProps<{
    name: IconName
    size?: SizeType
    color?: string
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
    /** 접근성 라벨 */
    ariaLabel?: string
    /** 접근성 역할 */
    role?: string
  }>(),
  {
    size: 'sm',
    styletype: 'default',
    role: 'img',
  },
)

/**
 * 기본 아이콘 매핑 (타입 안전)
 */
const baseIconMap: Record<BaseIconName, LucideIcon> = {
  house: House,
  user: User,
  settings: Settings,
  search: Search,
  plus: Plus,
  minus: Minus,
  squarePen: SquarePen,
  trash2: Trash2,
  save: Save,
  download: Download,
  upload: Upload,
  eye: Eye,
  eyeOff: EyeOff,
  heart: Heart,
  star: Star,
  check: Check,
  x: X,
  circleAlert: CircleAlert,
  info: Info,
  chevronDown: ChevronDown,
  chevronUp: ChevronUp,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  menu: Menu,
  ellipsis: Ellipsis,
  ellipsisVertical: EllipsisVertical,
  palette: Palette,
  // WMS 핵심 아이콘들
  package: Package,
  packageCheck: PackageCheck,
  packageX: PackageX,
  truck: Truck,
  warehouse: Warehouse,
  boxes: Boxes,
  archive: Archive,
  clipboardList: ClipboardList,
  clipboardCheck: ClipboardCheck,
  fileText: FileText,
  chartBar: ChartBar,
  trendingUp: TrendingUp,
  trendingDown: TrendingDown,
  calendar: Calendar,
  clock: Clock,
  mapPin: MapPin,
  table: Table,
  list: List,
  listFilter: ListFilter,
  arrowUpWideNarrow: ArrowUpWideNarrow,
  arrowDownWideNarrow: ArrowDownWideNarrow,
  scale: Scale,
  // 추가 아이콘들
  server: Server,
  database: Database,
  link: Link,
  arrowLeftRight: ArrowLeftRight,
  circleQuestionMark: CircleQuestionMark,
  monitor: Monitor,
  users: Users,
  code: Code,
  fileKey: FileKey,
  cpu: Cpu,
  rocket: Rocket,
  fileSpreadsheet: FileSpreadsheet,
  activity: Activity,
  messageCircle: MessageCircle,
  book: Book,
  // 인증 관련 아이콘들
  logIn: LogIn,
  logOut: LogOut,
  // 편집 관련 아이콘들
  copy: Copy,
  clipboard: Clipboard,
  undo2: Undo2,
  redo2: Redo2,
  pencil: Pencil,
  // 파일/폴더 관련 아이콘들
  folder: Folder,
  folderPlus: FolderPlus,
  folderOpen: FolderOpen,
  filePlus: FilePlus,
  fileSearch: FileSearch,
  filePen: FilePen,
  fileMinus: FileMinus,
  // 통신/알림 관련 아이콘들
  mail: Mail,
  send: Send,
  phone: Phone,
  bell: Bell,
  bellOff: BellOff,
  messageSquare: MessageSquare,
  // 화살표/방향 관련 아이콘들
  arrowUp: ArrowUp,
  arrowDown: ArrowDown,
  arrowLeft: ArrowLeft,
  arrowRight: ArrowRight,
  rotateCw: RotateCw,
  rotateCcw: RotateCcw,
  refreshCw: RefreshCw,
  move: Move,
  // 상태/피드백 관련 아이콘들
  circleCheckBig: CircleCheckBig,
  circleX: CircleX,
  circle: Circle,
  // 보안 관련 아이콘들
  lock: Lock,
  lockOpen: LockOpen,
  shield: Shield,
  shieldCheck: ShieldCheck,
  shieldAlert: ShieldAlert,
  // 사용자 관리 관련 아이콘들
  userPlus: UserPlus,
  userMinus: UserMinus,
  userCheck: UserCheck,
  userX: UserX,
  circleUser: CircleUser,
  // 액션 관련 아이콘들
  power: Power,
  zap: Zap,
  target: Target,
  flag: Flag,
  funnel: Funnel,
  // 조직/팀 관련 아이콘들
  building: Building,
  briefcase: Briefcase,
  globe: Globe,
  // 기타 유용한 아이콘들
  printer: Printer,
  share2: Share2,
  externalLink: ExternalLink,
  maximize2: Maximize2,
  minimize2: Minimize2,
  moveVertical: MoveVertical,
  moveHorizontal: MoveHorizontal,
  sun: Sun,
  moon: Moon,
}

/**
 * 동적 아이콘 로딩을 위한 함수 (나중에 확장 가능)
 * 현재는 사용하지 않지만 향후 커스텀 아이콘 지원을 위해 준비
 */
// const loadCustomIcon = async (iconName: string): Promise<LucideIcon | null> => {
//   try {
//     // 나중에 다른 아이콘 라이브러리나 커스텀 아이콘을 동적으로 로드할 수 있음
//     // 예: const { [iconName]: IconComponent } = await import('@/assets/icons')
//     return null
//   } catch {
//     return null
//   }
// }

/**
 * size -> 실제 크기 매핑
 */
const SIZE_PRESETS: Record<SizeType, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
}

/**
 * styletype -> color, class 매핑
 */
const STYLE_PRESETS: Record<StyleType, { color?: string; class: string }> = {
  default: { class: '' },
  primary: { 
    color: '#3b82f6',
    class: '',
  },
  success: { 
    color: '#10b981',
    class: '',
  },
  warning: { 
    color: '#f59e0b',
    class: '',
  },
  danger: { 
    color: '#ef4444',
    class: '',
  },
}

/** 최종 바인딩: 직접 넘긴 props가 있으면 styletype 기본값과 병합 */
const mapped = computed(() => {
  const stylePreset = STYLE_PRESETS[props.styletype!]
  const sizePreset = SIZE_PRESETS[props.size!]
  const finalClass = [stylePreset.class, props.class].filter(Boolean).join(' ')
  const ariaLabel = props.ariaLabel || props.name
  
  return {
    size: sizePreset,
    color: props.color || stylePreset.color, // props.color가 있으면 우선 사용, 없으면 stylePreset.color 사용
    class: finalClass,
    'aria-label': ariaLabel,
    'aria-hidden': !ariaLabel,
    role: props.role,
  }
})

/** 현재 아이콘 컴포넌트 */
const IconComponent = computed(() => {
  // 기본 아이콘 먼저 확인
  if (props.name in baseIconMap) {
    return baseIconMap[props.name as BaseIconName]
  }
  
  // 커스텀 아이콘은 나중에 확장
  // return loadCustomIcon(props.name)
  
  // 기본값으로 house 아이콘 반환
  return House
})
</script>

<template>
  <component 
    :is="IconComponent" 
    v-bind="mapped"
  />
</template>